import FeatureLockScreen from '../../components/apple-ui/FeatureLockScreen';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import TranslationProgress from '../../components/TranslationProgress';
import { Link } from 'react-router-dom';
import { MainLayoutApple, CardApple, ButtonApple, BadgeApple, ModalApple, GlobalContextBarApple } from '@/components/apple-ui';
import CostEstimateApple from '@/components/apple-ui/CostEstimateApple';
import ContextSelectorModal from '@/components/ContextSelectorModal';
import { usePlan } from '@/contexts/PlanContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useGlobalContext } from '@/contexts/GlobalContextProvider';
import { 
  PenTool, Copy, Loader2, Check, RefreshCw, Wand2, AlertCircle, BookOpen, Settings,
  ChevronDown, ChevronUp, Database, Info, X, Plus, CheckCircle, Zap, Download, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import '@/styles/apple-design-system.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'English', name: 'English' }, { code: 'Hindi', name: 'Hindi' }, { code: 'Gujarati', name: 'Gujarati' },
  { code: 'Tamil', name: 'Tamil' }, { code: 'Telugu', name: 'Telugu' }, { code: 'Punjabi', name: 'Punjabi' },
  { code: 'Marathi', name: 'Marathi' }, { code: 'Kannada', name: 'Kannada' }, { code: 'Malayalam', name: 'Malayalam' },
  { code: 'Bengali', name: 'Bengali' }, { code: 'Odia', name: 'Odia' }, { code: 'Assamese', name: 'Assamese' }, { code: 'Urdu', name: 'Urdu' },
];

const STYLE_OPTIONS = [
  { value: 'general', label: 'General', desc: 'Balanced tone for everyday content' },
  { value: 'formal', label: 'Formal', desc: 'Professional business communication' },
  { value: 'casual', label: 'Casual', desc: 'Friendly conversational tone' },
  { value: 'technical', label: 'Technical', desc: 'Preserve technical terminology' },
  { value: 'creative', label: 'Creative', desc: 'More expressive language' },
  { value: 'legal', label: 'Legal', desc: 'Precise legal terminology' },
  { value: 'medical', label: 'Medical', desc: 'Medical/healthcare content' },
];

const MTPEPageApple = ({ user, planInfo, onLogout }) => {
  // Theme and Global Context hooks
  const { isDarkMode } = useTheme();
  const { showContextModal, setShowContextModal, getContextIds } = useGlobalContext();
  const { refreshPlanInfo } = usePlan();
  const mtpeAccess = useFeatureAccess('mtpe');

  const [originalText, setOriginalText] = useState('');
  const [refinedText, setRefinedText] = useState('');
  const [isRefinedEdited, setIsRefinedEdited] = useState(false);
  const [language, setLanguage] = useState('Hindi');
  const [selectedStyle, setSelectedStyle] = useState('general');
  const [loading, setLoading] = useState(false);
  // Feature gating
  if (!mtpeAccess.hasAccess) {
    return <FeatureLockScreen feature="mtpe" requiredPlan={mtpeAccess.upgradeTo || 'starter'} currentPlan={mtpeAccess.currentPlan} />;
  }

  const [processingStep, setProcessingStep] = useState(0);
  const [mtpeStep, setMtpeStep] = useState(0);
  const [copied, setCopied] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  
  // Advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [instructions, setInstructions] = useState({
    fixGrammar: true, improveNaturalness: true, maintainMeaning: true,
    adaptIdioms: false, formalTone: false, keepTechnical: false
  });
  const [customInstructions, setCustomInstructions] = useState('');
  
  // Glossary state
  const [applyGlossary, setApplyGlossary] = useState(true);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [showGlossaryPanel, setShowGlossaryPanel] = useState(false);
  const [newTerm, setNewTerm] = useState({ source: '', target: '' });
  const glossaryFileInputRef = useRef(null);
  const [uploadingGlossary, setUploadingGlossary] = useState(false);
  
  // Memory state
  const [checkMemory, setCheckMemory] = useState(true);
  const [memorySuggestion, setMemorySuggestion] = useState(null);
  
  // Analysis state
  const [analysisData, setAnalysisData] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(true);
  
  // Warning modal state
  const [showWarning, setShowWarning] = useState(false);
  const [detectionData, setDetectionData] = useState(null);

  useEffect(() => {
    const words = originalText.trim().split(/\s+/).filter(Boolean);
    setWordCount(words.length);
  }, [originalText]);

  useEffect(() => { fetchGlossary(); }, []);

  const buildInstructions = useCallback(() => {
    let instructionText = '';
    if (instructions.fixGrammar) instructionText += '- Fix grammar and spelling\n';
    if (instructions.improveNaturalness) instructionText += '- Improve naturalness\n';
    if (instructions.maintainMeaning) instructionText += '- Maintain exact meaning\n';
    if (instructions.adaptIdioms) instructionText += '- Adapt idioms\n';
    if (instructions.formalTone) instructionText += '- Use formal tone\n';
    if (instructions.keepTechnical) instructionText += '- Keep technical terms\n';
    if (customInstructions) instructionText += `\nAdditional: ${customInstructions}`;
    return instructionText;
  }, [instructions, customInstructions]);

  const fetchGlossary = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/mtpe/glossary`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setGlossaryTerms(data.terms || []); }
    } catch (error) { console.error('Failed to fetch glossary:', error); }
  };

  const handleAddGlossaryTerm = async () => {
    if (!newTerm.source.trim() || !newTerm.target.trim()) { toast.error('Both terms required'); return; }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/mtpe/glossary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wrong_term: newTerm.source, correct_term: newTerm.target, language: language, notes: "" })
      });
      if (res.ok) {
        setGlossaryTerms(prev => [...prev, { source: newTerm.source, target: newTerm.target }]);
        setNewTerm({ source: '', target: '' });
        toast.success('Term added');
      } else { const data = await res.json(); toast.error(data.detail || 'Failed to add term'); }
    } catch (error) { toast.error('Failed to add term'); }
  };

  // Download glossary template (same format as CAT Tool)
  const handleDownloadGlossaryTemplate = () => {
    const template = `Source Term,Target Term,Context,Case Sensitive,Notes
company,कंपनी,Business context,No,Official translation
revenue,राजस्व,Financial term,No,Use this for all financial reports
growth,वृद्धि,General,No,
CEO,सीईओ,Title - keep in English with Hindi script,Yes,Do not translate
digital transformation,डिजिटल परिवर्तन,Technology,No,
machine learning,मशीन लर्निंग,Keep in English,Yes,Technical term - transliterate only`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mtpe_glossary_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Upload glossary from CSV
  const handleGlossaryFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingGlossary(true);
    try {
      const text = await file.text();
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const terms = results.data
            .filter(row => (row['Source Term'] || row['source']) && (row['Target Term'] || row['target']))
            .map(row => ({
              source: row['Source Term'] || row['source'] || '',
              target: row['Target Term'] || row['target'] || '',
              context: row['Context'] || row['context'] || '',
              caseSensitive: (row['Case Sensitive'] || row['case_sensitive'] || '').toLowerCase() === 'yes',
              notes: row['Notes'] || row['notes'] || ''
            }));

          if (terms.length === 0) {
            toast.error('No valid terms found');
            setUploadingGlossary(false);
            return;
          }

          // Add terms to state
          setGlossaryTerms(prev => [...prev, ...terms]);
          toast.success(`Imported ${terms.length} glossary terms`);
          setUploadingGlossary(false);
        },
        error: (error) => {
          toast.error('Failed to parse CSV');
          setUploadingGlossary(false);
        }
      });
    } catch (error) {
      toast.error('Failed to read file');
      setUploadingGlossary(false);
    }
    e.target.value = '';
  };

  const handleRefine = async (force = false) => {
    if (!originalText.trim()) { toast.error('Please enter text to refine'); return; }
    setLoading(true);
    setRefinedText('');
    setAnalysisData(null);
    setMemorySuggestion(null);
    setProcessingStep(1);
    setMtpeStep(1);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      const contextIds = getContextIds();
      formData.append('language', language);
      formData.append('machine_translation', originalText);
      formData.append('instructions', buildInstructions());
      formData.append('style', selectedStyle);
      formData.append('apply_glossary', applyGlossary ? 'true' : 'false');
      formData.append('check_source', force ? 'false' : 'true');
      formData.append('check_memory', checkMemory ? 'true' : 'false');
      formData.append('force', force ? 'true' : 'false');
      if (contextIds.instructionId) formData.append('instruction_id', contextIds.instructionId);
      if (contextIds.glossaryId || contextIds.projectId) formData.append('glossary_id', contextIds.glossaryId || contextIds.projectId);

      setProcessingStep(2);
      setMtpeStep(2);
      await new Promise(r => setTimeout(r, 300));
      setProcessingStep(3);
      setMtpeStep(3);

      const res = await fetch(`${API_URL}/api/mtpe/refine`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 402) {
          toast.error(data.detail?.error === 'insufficient_credits'
            ? `Insufficient balance. Need ${data.detail.required} words, have ${data.detail.balance}`
            : 'Insufficient balance. Please top up.');
        } else if (res.status === 429) { toast.error(data.detail?.message || 'Fair use limit exceeded'); }
        else { toast.error(data.detail || 'Refinement failed'); }
        setMtpeStep(0);
        return;
      }

      setProcessingStep(4);
      await new Promise(r => setTimeout(r, 200));

      if (data.detected) { setDetectionData(data); setShowWarning(true); setMtpeStep(0); return; }
      if (data.has_suggestion) { setMemorySuggestion(data.suggestion); toast.info('Found similar text in history!'); setMtpeStep(0); return; }

      setProcessingStep(5);
      setMtpeStep(4);
      setTimeout(() => setMtpeStep(0), 2000);
      setRefinedText(data.refined_text);
      setIsRefinedEdited(false);
      setAnalysisData(data.analysis);

      if (data.from_memory) { toast.success('Used previous refinement - no words deducted!'); }
      else if (data.credits_deducted !== undefined) { const cost = planInfo?.plan === 'payg' ? ` · ₹${(data.credits_deducted * 0.17).toFixed(2)} deducted` : ` · ${data.credits_deducted} words`; toast.success(`Refined!${cost}`); setTimeout(() => refreshPlanInfo(), 1500); }
      else { toast.success('Refinement complete!'); setTimeout(() => refreshPlanInfo(), 1500); }
    } catch (error) { toast.error('Refinement failed'); setMtpeStep(0); }
    finally { setLoading(false); setProcessingStep(0); }
  };

  const handleAcceptMemory = async () => {
    if (!memorySuggestion) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('language', language);
      formData.append('machine_translation', originalText);
      formData.append('use_memory', 'true');
      formData.append('memory_id', memorySuggestion.memory_id);
      formData.append('check_memory', 'false');
      formData.append('force', 'true');
      const res = await fetch(`${API_URL}/api/mtpe/refine`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (res.ok) { setRefinedText(data.refined_text); setMemorySuggestion(null); toast.success('Used previous - no words deducted!'); }
      else { toast.error(data.detail || 'Failed'); }
    } catch (error) { toast.error('Failed to use memory'); }
    finally { setLoading(false); }
  };

  const handleContinueAnyway = () => { setShowWarning(false); handleRefine(true); };
  const handleCopy = async () => { if (!refinedText) return; try { await navigator.clipboard.writeText(refinedText); setCopied(true); toast.success('Copied'); setTimeout(() => setCopied(false), 2000); } catch (error) { toast.error('Failed to copy'); } };
  const handleReRefine = () => { setIsRefinedEdited(false); handleRefine(); };
  const getProcessingLabel = () => { const labels = { 1: 'Analyzing...', 2: 'Checking glossary...', 3: 'Applying style...', 4: 'Generating...', 5: 'Finalizing...' }; return labels[processingStep] || 'Processing...'; };

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      {/* Context Selector Modal */}
      <ContextSelectorModal 
        isOpen={showContextModal} 
        onClose={() => setShowContextModal(false)} 
        variant="apple"
      />
      
      <div className="apple-content" data-testid="apple-mtpe-page">
        {/* Header */}
        <div className="apple-content-header">
          <h1 className="apple-content-title flex items-center gap-3">
            <PenTool className="text-purple-600" size={28} />
            Machine Translation Post-Editing
          </h1>
          <p className="apple-content-subtitle">Refine machine translations with our dual-layer engine for glossary enforcement and natural flow</p>
        </div>

        {/* Global Context Bar */}
        <GlobalContextBarApple 
          onOpenModal={() => setShowContextModal(true)} 
          isDarkMode={isDarkMode}
        />

        {/* Settings Bar */}
        <CardApple className="mb-6">
          <CardApple.Body>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-48">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Target Language</label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className="apple-input apple-select" data-testid="mtpe-language-select">
                  {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                </select>
              </div>
              <div className="w-48">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Style</label>
                <select value={selectedStyle} onChange={(e) => setSelectedStyle(e.target.value)} className="apple-input apple-select" data-testid="mtpe-style-select">
                  {STYLE_OPTIONS.map(style => <option key={style.value} value={style.value}>{style.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-4 ml-auto">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="mtpe-glossary-toggle">
                  <input type="checkbox" checked={applyGlossary} onChange={(e) => setApplyGlossary(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                  <span className="text-sm text-gray-600">Apply Glossary</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="mtpe-memory-toggle">
                  <input type="checkbox" checked={checkMemory} onChange={(e) => setCheckMemory(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                  <span className="text-sm text-gray-600">Check Memory</span>
                </label>
                <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors" data-testid="mtpe-advanced-toggle">
                  <Settings size={16} />Advanced{showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>
            </div>

            {/* Advanced Settings */}
            {showAdvanced && (
              <div className="mt-4 pt-4 border-t border-gray-100" data-testid="mtpe-advanced-panel">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries({ fixGrammar: 'Fix grammar & spelling', improveNaturalness: 'Improve naturalness', maintainMeaning: 'Maintain exact meaning', adaptIdioms: 'Adapt idioms', formalTone: 'Formal tone', keepTechnical: 'Keep technical terms' }).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                      <input type="checkbox" checked={instructions[key]} onChange={(e) => setInstructions(prev => ({ ...prev, [key]: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                      <span className="text-sm text-gray-600">{label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-4">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Custom Instructions</label>
                  <input type="text" value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)} placeholder="Add specific instructions..." className="apple-input" data-testid="mtpe-custom-instructions" />
                </div>
              </div>
            )}
          </CardApple.Body>
        </CardApple>

        {/* Processing Progress */}
        {mtpeStep > 0 && (
          <TranslationProgress
            step={mtpeStep}
            steps={['Preparing Content', 'Post-Editing Segments', 'Refining Output', 'Post-Edit Complete']}
          />
        )}

        {/* Main Editor */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <CardApple>
            <CardApple.Header>
              <div className="flex items-center justify-between w-full">
                <CardApple.Title>Machine Translation Input</CardApple.Title>
                <span className="text-sm text-gray-500">{wordCount} words</span>
              </div>
            </CardApple.Header>
            <CardApple.Body>
              <textarea value={originalText} onChange={(e) => setOriginalText(e.target.value)} placeholder="Paste your machine translation here..." className="w-full min-h-[300px] p-4 border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base" data-testid="mtpe-input" />
              {planInfo?.plan === 'payg' && wordCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 4 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED' }}>≈ ₹{(wordCount * 0.75 * 0.17).toFixed(2)}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{Math.ceil(wordCount * 0.75)} words × ₹0.17 (75% rate)</div>
                  </div>
                </div>
              )}
              <ButtonApple onClick={() => handleRefine(false)} disabled={loading || !originalText.trim()} fullWidth className="mt-4" icon={loading ? Loader2 : Wand2} data-testid="mtpe-refine-btn">
                {loading ? getProcessingLabel() : 'Refine Translation'}
              </ButtonApple>
            </CardApple.Body>
          </CardApple>

          {/* Output Panel */}
          <CardApple>
            <CardApple.Header>
              <div className="flex items-center justify-between w-full">
                <CardApple.Title>Refined Output</CardApple.Title>
                <div className="flex items-center gap-2">
                  {isRefinedEdited && <button onClick={handleReRefine} className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700" data-testid="mtpe-rerefine-btn"><RefreshCw size={14} />Re-refine</button>}
                  {refinedText && <button onClick={handleCopy} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900" data-testid="mtpe-copy-btn">{copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}{copied ? 'Copied' : 'Copy'}</button>}
                </div>
              </div>
            </CardApple.Header>
            <CardApple.Body>
              <textarea value={refinedText} onChange={(e) => { setRefinedText(e.target.value); setIsRefinedEdited(true); }} placeholder="Refined translation will appear here..." className="w-full min-h-[300px] p-4 border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base" data-testid="mtpe-output" />
              
              {/* Analysis Results */}
              {analysisData && showAnalysis && (
                <div className={`mt-4 p-4 rounded-2xl ${isDarkMode ? 'bg-slate-800' : 'bg-purple-50'}`} data-testid="mtpe-analysis">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-purple-900">Quality Analysis</span>
                    <button onClick={() => setShowAnalysis(false)} className="text-purple-400 hover:text-purple-600"><X size={16} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    {analysisData.improvement && <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}><div className="text-xs text-gray-500 mb-1">Improvement</div><div className="font-semibold text-purple-700">{analysisData.improvement.score || analysisData.improvement.percentage || 'N/A'}</div></div>}
                    {analysisData.fluency && <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}><div className="text-xs text-gray-500 mb-1">Fluency</div><div className="font-semibold text-purple-700">{analysisData.fluency}</div></div>}
                    {analysisData.accuracy && <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}><div className="text-xs text-gray-500 mb-1">Accuracy</div><div className="font-semibold text-purple-700">{analysisData.accuracy}</div></div>}
                  </div>
                  {analysisData.changes?.length > 0 && (
                    <div className="mt-3"><div className="text-xs text-gray-500 mb-2">Changes Made:</div><ul className="text-sm text-purple-800 space-y-1">{analysisData.changes.slice(0, 3).map((change, idx) => (<li key={idx} className="flex items-start gap-2"><CheckCircle size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />{change}</li>))}</ul></div>
                  )}
                </div>
              )}
            </CardApple.Body>
          </CardApple>
        </div>

        {/* Cost Estimate */}
        {wordCount > 0 && (
          <div className="mt-6">
            <CostEstimateApple planInfo={planInfo}
              feature="mtpe"
              targetWords={wordCount}
            />
          </div>
        )}

        {/* Glossary Panel */}
        <CardApple className="mt-6">
          <div className="p-4 cursor-pointer" onClick={() => setShowGlossaryPanel(!showGlossaryPanel)} data-testid="mtpe-glossary-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen size={18} className="text-green-600" />
                <span className="font-semibold text-gray-900">Glossary Terms</span>
                <span className="text-sm text-gray-500">({glossaryTerms.length} terms)</span>
              </div>
              {showGlossaryPanel ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </div>
          {showGlossaryPanel && (
            <CardApple.Body className="pt-0" data-testid="mtpe-glossary-panel">
              {/* Upload/Download Buttons */}
              <div className="flex items-center gap-2 mb-4">
                <input type="file" ref={glossaryFileInputRef} accept=".csv" onChange={handleGlossaryFileUpload} className="hidden" />
                <ButtonApple variant="secondary" size="sm" icon={Download} onClick={handleDownloadGlossaryTemplate} data-testid="mtpe-download-template-btn">
                  Template
                </ButtonApple>
                <ButtonApple variant="secondary" size="sm" icon={Upload} onClick={() => glossaryFileInputRef.current?.click()} disabled={uploadingGlossary} data-testid="mtpe-upload-glossary-btn">
                  {uploadingGlossary ? 'Uploading...' : 'Upload CSV'}
                </ButtonApple>
              </div>
              
              {/* Add Term Form */}
              <div className="flex items-end gap-3 mb-4">
                <div className="flex-1"><label className="text-xs font-medium text-gray-500 mb-1 block">Source Term</label><input type="text" value={newTerm.source} onChange={(e) => setNewTerm(prev => ({ ...prev, source: e.target.value }))} placeholder="Original term" className="apple-input" data-testid="mtpe-glossary-source" /></div>
                <div className="flex-1"><label className="text-xs font-medium text-gray-500 mb-1 block">Target Term</label><input type="text" value={newTerm.target} onChange={(e) => setNewTerm(prev => ({ ...prev, target: e.target.value }))} placeholder="Translation" className="apple-input" data-testid="mtpe-glossary-target" /></div>
                <ButtonApple variant="secondary" onClick={handleAddGlossaryTerm} icon={Plus} data-testid="mtpe-add-glossary-btn">Add</ButtonApple>
              </div>
              
              {/* Terms List with Scroll */}
              {glossaryTerms.length > 0 ? (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Target</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {glossaryTerms.map((term, idx) => (
                        <tr key={idx} className="hover:bg-gray-50" data-testid={`glossary-term-${idx}`}>
                          <td className="px-3 py-2 text-gray-700">{term.source}</td>
                          <td className="px-3 py-2 text-green-700 font-medium">{term.target}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (<p className="text-sm text-gray-500 text-center py-4">No glossary terms yet. Add terms manually or upload a CSV file.</p>)}
              {glossaryTerms.length > 0 && <Link to="/glossary" className="block text-center text-sm text-purple-600 hover:underline mt-3">Manage all glossary terms</Link>}
            </CardApple.Body>
          )}
        </CardApple>

        {/* Info Card */}
        <div className="mt-6 p-5 bg-purple-50 border border-purple-100 rounded-2xl">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-purple-900 mb-2">How MTPE Works</h3>
              <ol className="text-sm text-purple-700 space-y-1">
                <li>1. <strong>Paste MT Output</strong> - Enter your raw machine translation</li>
                <li>2. <strong>Select Style & Options</strong> - Choose tone, enable glossary enforcement</li>
                <li>3. <strong>Refine</strong> - Quality layer applies your glossary and improves natural flow</li>
                <li>4. <strong>Edit & Copy</strong> - Make final edits and copy the result</li>
              </ol>
              <div className="mt-3 flex items-center gap-4 text-xs text-purple-600">
                <span className="flex items-center gap-1"><Zap size={12} />Memory saves repeated work</span>
                <span className="flex items-center gap-1"><BookOpen size={12} />Glossary ensures consistency</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Memory Suggestion Modal */}
      {memorySuggestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-3xl shadow-2xl w-full max-w-lg mx-4 p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-testid="mtpe-memory-modal">
            <div className="flex items-center gap-3 mb-4"><Database size={24} className="text-purple-600" /><h3 className="text-xl font-semibold">Found Similar Text!</h3></div>
            <p className="text-sm text-gray-600 mb-4">We found similar text in your refinement history. Using it won't deduct any words.</p>
            <div className="p-4 bg-purple-50 rounded-2xl mb-4"><div className="text-xs text-gray-500 mb-1">Previous Refinement:</div><p className="text-sm text-gray-800">{memorySuggestion.refined_text?.substring(0, 200)}...</p><div className="text-xs text-purple-600 mt-2">{memorySuggestion.similarity}% match</div></div>
            <div className="flex items-center justify-end gap-3">
              <ButtonApple variant="ghost" onClick={() => { setMemorySuggestion(null); handleRefine(true); }}>Refine Anyway</ButtonApple>
              <ButtonApple onClick={handleAcceptMemory} data-testid="mtpe-accept-memory-btn">Use Previous</ButtonApple>
            </div>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      {showWarning && detectionData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-3xl shadow-2xl w-full max-w-lg mx-4 p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-testid="mtpe-warning-modal">
            <div className="flex items-center gap-3 mb-4"><AlertCircle size={24} className="text-amber-500" /><h3 className="text-xl font-semibold">Source Text Detected</h3></div>
            <p className="text-sm text-gray-600 mb-4">The text appears to be source language ({detectionData.detected_language}), not MT output. MTPE works best with MT output.</p>
            <div className="flex items-center justify-end gap-3">
              <ButtonApple variant="ghost" onClick={() => setShowWarning(false)}>Go Back</ButtonApple>
              <ButtonApple variant="warning" onClick={handleContinueAnyway} data-testid="mtpe-continue-anyway-btn">Continue Anyway</ButtonApple>
            </div>
          </div>
        </div>
      )}
    </MainLayoutApple>
  );
};

export default MTPEPageApple;
